import { collection, query, where, getDocs, getDoc, doc, getAggregateFromServer, sum, Timestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { LedgerTransaction, SubCategoryBudget } from "../global/types";
import dayjs from "dayjs";
import { crossRate } from "../utils/balanceConversion";
import { fetchLatestDailyRateDoc } from "./dailyRatesCache";

export type BudgetCheckResult = {
    isOverBudget: boolean;
    isApproachingLimit: boolean; // 80% threshold
    budget: number;
    spent: number;
    percentUsed: number;
} | null;

/**
 * A budget is set in the user's reporting currency; a transaction can land on any account's own
 * currency. Summing raw `amount` across accounts would add EUR and RUB face values together, so
 * the aggregate sums each row's own systemAmountUsd instead, then the USD total — like the
 * individual amounts adjusted below it — is marked to market at the latest Daily Rate, the same
 * way an account balance is. A row with no snapshot yet (rare after the T19 backfill) contributes
 * nothing to the sum; there is no UI here to show it as excluded, so it is under- rather than
 * mis-counted.
 */
export async function checkBudgetOverspending(
    userId: string,
    transaction: Pick<LedgerTransaction, "categoryId" | "amount" | "currency" | "date">,
    reportingCurrency: string,
    excludeTransactionId?: string
): Promise<BudgetCheckResult> {
    const date = dayjs(transaction.date.toDate());
    const yearMonth = date.format('YYYY-M'); // Format used in subCategoryBudgets: YYYY-1, YYYY-12

    // Query categoryId as-is, including null, so uncategorized transactions correctly resolve as having no budget.
    const budgetsRef = collection(db, "subCategoryBudgets");
    const qBudget = query(
        budgetsRef,
        where("userId", "==", userId),
        where("subCategoryId", "==", transaction.categoryId),
        where("yearMonth", "==", yearMonth)
    );

    const budgetSnapshot = await getDocs(qBudget);
    if (budgetSnapshot.empty) return null;

    const budgetData = budgetSnapshot.docs[0].data() as SubCategoryBudget;
    const budgetAmount = budgetData.budget;

    const latest = await fetchLatestDailyRateDoc();
    const usdToReporting = crossRate('USD', reportingCurrency, latest);
    if (usdToReporting === null) return null;

    const startOfMonth = date.startOf('month').toDate();
    const endOfMonth = date.endOf('month').toDate();

    const transactionsRef = collection(db, "ledger_transactions");
    const qTransactions = query(
        transactionsRef,
        where("userId", "==", userId),
        where("categoryId", "==", transaction.categoryId),
        where("date", ">=", Timestamp.fromDate(startOfMonth)),
        where("date", "<=", Timestamp.fromDate(endOfMonth))
    );

    const transactionsAggregate = await getAggregateFromServer(qTransactions, { total: sum("systemAmountUsd") });
    let totalSpent = (transactionsAggregate.data().total || 0) * usdToReporting;

    // Exclude the current transaction's prior amount if this is an update
    if (excludeTransactionId) {
        const excludedSnap = await getDoc(doc(db, "ledger_transactions", excludeTransactionId));
        if (excludedSnap.exists()) {
            const excludedData = excludedSnap.data() as LedgerTransaction;
            const excludedDate = excludedData.date.toDate();
            const matchesCurrentScope = excludedData.categoryId === transaction.categoryId
                && excludedDate >= startOfMonth && excludedDate <= endOfMonth;
            if (matchesCurrentScope) {
                const rate = crossRate(excludedData.currency, reportingCurrency, latest);
                if (rate !== null) totalSpent -= excludedData.amount * rate;
            }
        }
    }

    // This transaction has no snapshot of its own yet — it runs before the row is committed.
    const transactionRate = crossRate(transaction.currency, reportingCurrency, latest);
    if (transactionRate !== null) totalSpent += transaction.amount * transactionRate;

    const percentUsed = budgetAmount > 0 ? (totalSpent / budgetAmount) * 100 : 0;
    const isOverBudget = totalSpent > budgetAmount;
    const isApproachingLimit = !isOverBudget && percentUsed >= 80;

    return {
        isOverBudget,
        isApproachingLimit,
        budget: budgetAmount,
        spent: totalSpent,
        percentUsed
    };
}
